import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from '../../store/store';
import { setDispatch, setNavigate } from '../../utils/navigation';
import Header from '../Header/Header';
import modalRegistry from '../Modal/modalRegistry';
import ModalWrapper from '../Modal/ModalWrapper/ModalWrapper';
import Preloader from '../Preloader/Preloader';
import ProtectedRoute from '../ProtectedRoute';
import RegistrationCookieCleaner from '../RegistrationCookieCleaner';
import Sidebar from '../Sidebar/Sidebar';
import SidebarInfoBlock from '../SidebarInfoBlock/SidebarInfoBlock';
import styles from './styles/styles.module.css';

// Каждая страница - отдельный чанк, подгружается только когда пользователь
// реально на неё переходит, а не разом со всем приложением при первой
// загрузке. За обычную сессию открывается 3-5 страниц из 20+ - раньше все
// они грузились одним куском в 1,24 МБ независимо от того, нужны ли.
const AccountPage = lazy(() => import('../../pages/AccountPage/AccountPage'));
const ContactsPage = lazy(() => import('../../pages/ContactsPage/ContactsPage'));
const EditOrder = lazy(() => import('../EditOrder/EditOrder'));
const Faq = lazy(() => import('../../pages/Faq/Faq'));
const Homepage = lazy(() => import('../../pages/Homepage/Homepage'));
const InitPage = lazy(() => import('../../pages/InitPage/InitPage'));
const LoginPage = lazy(() => import('../../pages/LoginPage/LoginPage'));
const NotificationsPage = lazy(
  () => import('../../pages/NotificationsPage/NotificationsPage')
);
const Orders = lazy(() => import('../../pages/Orders/Orders'));
const Prices = lazy(() => import('../../pages/Prices/Prices'));
const RefundPage = lazy(() => import('../../pages/RefundPage/RefundPage'));
const RegistrationCompanyContactsPage = lazy(
  () =>
    import('../../pages/RegistrationCompanyContactsPage/RegistrationCompanyContactsPage')
);
const RegistrationCompanyDetailsPage = lazy(
  () =>
    import('../../pages/RegistrationCompanyDetailsPage/RegistrationCompanyDetailsPage')
);
const RegistrationCompanyPage = lazy(
  () => import('../../pages/RegistrationCompanyPage/RegistrationCompanyPage')
);
const RegistrationPage = lazy(
  () => import('../../pages/RegistrationPage/RegistrationPage')
);
const RegistrationUserDetailsPage = lazy(
  () => import('../../pages/RegistrationUserDetailsPage/RegistrationUserDetailsPage')
);
const RegistrationUserPage = lazy(
  () => import('../../pages/RegistrationUserPage/RegistrationUserPage')
);
const SendPage = lazy(() => import('../../pages/SendPage/SendPage'));
const Wallet = lazy(() => import('../../pages/Wallet/Wallet'));

function App() {
  const { error, fetchStatus } = useAppSelector((state) => state.user);
  const isLoading = fetchStatus === 'loading';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Регистрируем navigate/dispatch для кода вне React-дерева (перехватчик
  // axios в api.js) - см. src/utils/navigation.js. App - самый верхний
  // компонент внутри <BrowserRouter>/<Provider>, поэтому именно здесь.
  useEffect(() => {
    setNavigate(navigate);
    setDispatch(dispatch);
  }, [navigate, dispatch]);

  console.log(error, isLoading);
  return (
    <>
      <RegistrationCookieCleaner />
      <ModalWrapper popups={modalRegistry} />
      <Routes>
        <Route
          path="/init"
          element={
            <Suspense fallback={<Preloader className={styles.routeLoader} />}>
              <InitPage />
            </Suspense>
          }
        />
        <Route
          path="/login"
          element={
            <Suspense fallback={<Preloader className={styles.routeLoader} />}>
              <LoginPage />
            </Suspense>
          }
        />
        <Route
          path="/registration"
          element={
            <Suspense fallback={<Preloader className={styles.routeLoader} />}>
              <RegistrationPage />
            </Suspense>
          }
        />
        <Route
          path="/registration/phone"
          element={
            <Suspense fallback={<Preloader className={styles.routeLoader} />}>
              <RegistrationUserPage />
            </Suspense>
          }
        />
        <Route
          path="/registration/user/details"
          element={
            <Suspense fallback={<Preloader className={styles.routeLoader} />}>
              <RegistrationUserDetailsPage />
            </Suspense>
          }
        />
        <Route
          path="/registration/company"
          element={
            <Suspense fallback={<Preloader className={styles.routeLoader} />}>
              <RegistrationCompanyPage />
            </Suspense>
          }
        />
        <Route
          path="/registration/company/details"
          element={
            <Suspense fallback={<Preloader className={styles.routeLoader} />}>
              <RegistrationCompanyDetailsPage />
            </Suspense>
          }
        />
        <Route
          path="/registration/company/contacts"
          element={
            <Suspense fallback={<Preloader className={styles.routeLoader} />}>
              <RegistrationCompanyContactsPage />
            </Suspense>
          }
        />
        <Route path="/login/menu" element={<SidebarInfoBlock />} />
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <div className={styles.App}>
                {isLoading || error ? null : <Sidebar />}
                <div className={styles.mainLayout}>
                  <Header />
                  <div className={styles.mainContent}>
                    <div className={styles.pagesWrapper}>
                      {/* Один Suspense на все внутренние маршруты, а не
                          снаружи всего приложения - иначе при обычной
                          навигации между страницами (Главная -> Заказы)
                          пропадали бы Header и Sidebar вместе с контентом,
                          а не только сам контент. */}
                      <Suspense fallback={<Preloader className={styles.routeLoader} />}>
                        <Routes>
                          <Route path="/" element={<Homepage />} />
                          <Route path="/orders" element={<Orders />} />
                          <Route path="/orders/:orderNumber" element={<EditOrder />} />
                          <Route path="/balance" element={<Wallet />} />
                          <Route path="/prices" element={<Prices />} />
                          <Route path="/send" element={<SendPage />} />
                          <Route path="/refund" element={<RefundPage />} />
                          <Route path="/add" element={<RefundPage />} />
                          <Route path="/account" element={<AccountPage />} />
                          <Route path="/faq" element={<Faq />} />
                          <Route path="/contacts" element={<ContactsPage />} />
                          <Route path="/menu" element={<SidebarInfoBlock />} />
                          <Route path="/notifications" element={<NotificationsPage />} />
                        </Routes>
                      </Suspense>
                    </div>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

export default App;
